import React from 'react';

import {assert, assertAndContinue} from 'Asserts';
import {ProcedureType, StudyType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {UpdateProcedureRunResponse} from 'procedures/network/ProcedureRunDAO';
import {navigateToStudyOverviewTab} from 'studies/FileUtils';
import {ExecutionPlan} from 'studies/progress/Types';
import {
  CreationTrayCallbacks,
  OnUnknownErrorAction,
  showCreationTrayTask,
} from 'studies/sidepanel/CreationTrayTask';
import {refreshProcedureRunDetailsView} from 'studies/sidepanel/TrayTaskUtils';
import {
  getProcedureRunConditionTextForProcedureType,
  getProcedureRunUIStringForStudyType,
} from 'studies/TypedStudyUtils';
import {BasePendingTask} from 'util/resume-persisted-tasks/PersistedTaskTypes';
import * as TaskRunner from 'util/TaskRunner';

import {getTypedStudyAdapter} from '../TypedStudyAdapters';

export interface AddConditionsTaskRequestMetadataUpdate {
  id: string;
  studyId: string;
  studyType: StudyType;
  procedureType: ProcedureType;
  numNewConditions: number;
}

export interface AddConditionsTaskRequestMetadata {
  updates: Array<AddConditionsTaskRequestMetadataUpdate>;
}

export type ConditionCreatePendingTask = BasePendingTask<
  'CONDITION',
  AddConditionsTaskRequestMetadata,
  UpdateProcedureRunResponse
>;

export function getCreationDescriptor(
  procedureType: ProcedureType,
  totalNumNewConditions: number
): JSX.Element {
  return (
    <>{`new worksheets to add ${totalNumNewConditions} ${getProcedureRunConditionTextForProcedureType(procedureType, {plural: totalNumNewConditions > 1})}`}</>
  );
}
export function showConditionCreationTrayTask(
  taskId: string,
  statusUrl: string,
  taskMeta: TaskRunner.TaskMeta,
  updates: Array<AddConditionsTaskRequestMetadataUpdate>,
  callbacks: CreationTrayCallbacks<UpdateProcedureRunResponse>,
  timingMeta?: ExecutionPlan,
  persistedPendingTask?: ConditionCreatePendingTask
): void {
  assert(updates.length > 0, 'Expected at least one update');
  assertAndContinue(
    new Set(updates.map((update) => update.procedureType)).size === 1,
    'Expected all updates to have the same procedureType'
  );
  const procedureType = updates[0].procedureType;
  assertAndContinue(
    new Set(updates.map((update) => update.studyType)).size === 1,
    'Expected all updates to have the same studyType'
  );
  const studyType = updates[0].studyType;
  assert(
    getTypedStudyAdapter(studyType).isMultiExperimentEnabled,
    'Adding conditions is only be supported for multi-experiment studies'
  );
  const totalNumNewConditions = updates.reduce((acc, update) => acc + update.numNewConditions, 0);
  const conditionsString = getProcedureRunConditionTextForProcedureType(procedureType, {
    plural: totalNumNewConditions > 1,
  });

  showCreationTrayTask<'CONDITION', AddConditionsTaskRequestMetadata, UpdateProcedureRunResponse>(
    taskId,
    statusUrl,
    taskMeta,
    {
      creation: (
        <>{`new worksheets to add ${totalNumNewConditions} ${getProcedureRunConditionTextForProcedureType(procedureType, {plural: totalNumNewConditions > 1})}`}</>
      ),
      creationErrorHeader: `Creating ${conditionsString} failed.`,
      taskStatusErrorHeader: `Failed to fetch ${conditionsString} creation progress.`,
      success: (
        <>{`${totalNumNewConditions} ${conditionsString} added to ${getProcedureRunUIStringForStudyType(studyType)}`}</>
      ),
    },
    callbacks,
    timingMeta,
    persistedPendingTask
  );
}

export function getConditionCreationUnknownErrorAction(
  studyId: string,
  studyType: StudyType,
  procedureRunId: string,
  procedureType: ProcedureType,
  numNewConditions: number,
  onError?: () => void
): OnUnknownErrorAction {
  assert(
    getTypedStudyAdapter(studyType).isMultiExperimentEnabled,
    'Adding conditions is only be supported for multi-experiment studies'
  );
  const procedureRunString = getProcedureRunUIStringForStudyType(studyType);
  return {
    text: `View ${procedureRunString}`,
    description: (
      <>
        Click <i>View {procedureRunString}</i> to manually verify whether the{' '}
        {getProcedureRunConditionTextForProcedureType(procedureType, {
          plural: numNewConditions > 1,
        })}{' '}
        {numNewConditions > 1 ? 'were' : 'was'} created. If not, consider retrying.
      </>
    ),
    onClick: () => {
      navigateToStudyOverviewTab(studyId, procedureRunId);
    },
    onShowErrorToast: onError,
  };
}

export async function onSuccessAfterAddConditions(procedureRunId: string): Promise<void> {
  await refreshProcedureRunDetailsView(procedureRunId);
}

export function onOpenAfterAddConditions(studyId: string, procedureRunId: string): void {
  navigateToStudyOverviewTab(studyId, procedureRunId);
}
